import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE } from 'api/utilityActionTypes';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';

const getFormFields = ({
  onAutocompleteAddress,
  addressMatches,
  setAddressMatches,
  shouldRenderSuggestions,
  setShouldRenderSuggestions,
  fieldTitle,
  formValues = {},
}) => {
  return [
    {
      id: 'addressInput',
      title: fieldTitle,
      type: 'autocomplete',
      properties: {
        placeholder: 'type address here...',
        choices: (addressMatches || [])
          .filter(
            ({ id, display }) =>
              formValues.address?.id !== id || formValues.addressInput !== display,
          )
          .map(({ display }) => display),
        onSuggestionSelected: ({ newValue, formikBag }) => {
          const addressMatch = (addressMatches || []).find(({ display }) => newValue === display);
          formikBag.setFieldValue('address', addressMatch);
          formikBag.setFieldValue('addressInput', newValue);
          setAddressMatches([]);
          setShouldRenderSuggestions(false);
        },
        shouldRenderSuggestions: () => shouldRenderSuggestions,
        filterFunc: () => {
          return true;
        },
        autoFocus: true,
        alwaysRenderSuggestions: !formValues.address,
        onChange: onAutocompleteAddress,
      },
    },
    {
      id: 'address',
      type: 'hidden',
    },
  ];
};

const UPAddressSearchForm = ({
  dispatch,
  onSubmit,
  buttonText,
  fieldTitle,
  utilityPartnerId,
  disabled,
}) => {
  const [addressMatches, setAddressMatches] = useState([]);
  const [shouldRenderSuggestions, setShouldRenderSuggestions] = useState(false);
  const lastAutocompleteSearchRef = useRef(null);

  const onAutocompleteAddress = ({ newValue, formikBag }) => {
    const { values } = formikBag;
    lastAutocompleteSearchRef.current = newValue;
    setShouldRenderSuggestions(true);

    if (values.address && values.addressInput !== newValue) {
      formikBag.setFieldValue('address', null);
    }

    if (!newValue) {
      setAddressMatches([]);
    } else {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE,
        queryId: newValue,
        payload: {
          input: newValue,
          utilityPartnerId,
          withRequiredTypes: true,
          includeGoogleMapsSuggestions: false,
        },
      }).then((addresses) => {
        if (lastAutocompleteSearchRef.current === newValue) {
          setAddressMatches(addresses);
        }
      })
        .catch(() => {
          if (lastAutocompleteSearchRef.current === newValue) {
            setAddressMatches([]);
          }
        });
    }
  };

  const _handleSubmit = ({ addressInput, address }) => {
    // Only submit if an address was actually selected
    if (address && address.id) {
      onSubmit({ address, addressInput });
    }
    // If no address selected, do nothing (no error, no callback)
  };

  const formFields = getFormFields({
    addressMatches,
    onAutocompleteAddress,
    shouldRenderSuggestions,
    setShouldRenderSuggestions,
    setAddressMatches,
    fieldTitle,
  });

  return (
    <Formik
      initialValues={createInitialValues({ formFields })}
      enableReinitialize
      validate={createFormValidationFn(formFields)}
      onSubmit={_handleSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          addressMatches,
          onAutocompleteAddress,
          setAddressMatches,
          shouldRenderSuggestions,
          setShouldRenderSuggestions,
          formValues: formikBag.values,
          fieldTitle,
        });

        const shouldShowContinueButton = !!formikBag?.values?.addressInput;

        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            <SrBox paddingTop="24px" display={shouldShowContinueButton ? 'block' : 'none'}>
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                disabled={disabled || !formikBag.values.address}
                heightType={SR_BUTTON_HEIGHT_MEDIUM}
                hasBoxShadow
                type="submit"
              >
                {buttonText}
              </SrButton>
            </SrBox>
          </Form>
        );
      }}
    />
  );
};

UPAddressSearchForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  buttonText: PropTypes.string.isRequired,
  fieldTitle: PropTypes.string.isRequired,
  utilityPartnerId: PropTypes.number,
  disabled: PropTypes.bool,
};

UPAddressSearchForm.displayName = 'UPAddressSearchForm';

export default UPAddressSearchForm;
